import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { UnitBase } from 'hooks/useDisplayUnits/useDisplayUnits';
import { i18n } from 'i18n';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import { createProjectNumberFormatter, round } from 'utils';
import { isCustomUnitField } from 'utils/unitConversions';
import { useContext } from 'react';
import ProjectContext from 'modules/project/providers/project/context';

interface UnitSystemValueProps {
  value?: number;
  unitSystem: UnitSystem;
  unitInfo: UnitBase;
  decimalPlaces: number;
  customUnitFields: CustomUnitFields[];
}

export const unitSystemRawValue = ({ value, unitSystem, unitInfo, decimalPlaces, customUnitFields }: UnitSystemValueProps) => {
  if (value !== undefined && isCustomUnitField(unitSystem, customUnitFields, unitInfo.customUnitField)) {
    return round(unitInfo.imperial.conversion(value), decimalPlaces);
  }

  return round(value as number, decimalPlaces);
};

export const UnitSystemValue = ({ value, unitSystem, unitInfo, decimalPlaces, customUnitFields }: UnitSystemValueProps) => {
  const projectContext = useContext(ProjectContext);
  const formatNumber = createProjectNumberFormatter(
    projectContext?.state.decimalSeparator,
    projectContext?.state.thousandsSeparator,
  );
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitInfo.customUnitField);
  let numericValue: number | undefined;
  if (value !== undefined) {
    numericValue = isCustom
      ? round(unitInfo.imperial.conversion(value), decimalPlaces)
      : round(value, decimalPlaces);
  }
  const formattedValue = value !== undefined
    ? formatNumber(numericValue as number, decimalPlaces)
    : '';
  const abbreviation = value !== undefined && isCustom
    ? unitInfo.imperial.abbreviation
    : unitInfo.metric.abbreviation;
  // Abbreviations are already localised by the unit tables (see localiseUnitOfMeasurement).
  const displayAbbreviation = abbreviation ?? '';

  return (
    <span>
      {formattedValue}
      {formattedValue && displayAbbreviation ? ' ' : ''}
      {displayAbbreviation}
    </span>
  );
};
interface UnitSystemDetailProps {
  designed?: number;
  selected?: number;
  unitSystem: UnitSystem;
  unitInfo: UnitBase;
  decimalPlaces: number;
  customUnitFields: CustomUnitFields[];
}

const UnitSystemDetail = ({ designed, selected, unitSystem, unitInfo, decimalPlaces, customUnitFields }: UnitSystemDetailProps) => (
  <TableRow key={unitInfo.name}>
    <TableCell component="th" scope="row">
      {i18n._(unitInfo.name)}
    </TableCell>
    <TableCell align="right">
      {
        designed !== undefined && (
          <UnitSystemValue
            value={designed}
            unitInfo={unitInfo}
            unitSystem={unitSystem}
            decimalPlaces={decimalPlaces}
            customUnitFields={customUnitFields}
          />
        )
      }
    </TableCell>
    <TableCell align="right">
      <UnitSystemValue
        value={selected}
        unitInfo={unitInfo}
        unitSystem={unitSystem}
        decimalPlaces={decimalPlaces}
        customUnitFields={customUnitFields}
      />
    </TableCell>
  </TableRow>
);

export default UnitSystemDetail;
